import { useEffect, type ComponentType } from "react";
import { ConsoleShell, EmptyState, PageHeader, type ConsoleDestination } from "@dashboard-intelligent/ui";
import { INDUSTRY_SLUGS, type IndustrySlug } from "@dashboard-intelligent/api/src/modules/industry/types";
import { endpoints } from "./endpoints";
import { IndustryProvider } from "./industry";
import { IndustrySwitcher } from "./IndustrySwitcher";
import { ROUTES, routeFromPath, usePathname, type RouteId } from "./router";
import { industryUrl, tenantFrom, type Tenant } from "./tenant";
import { useApi } from "./useApi";
import { DashboardPage } from "./pages/Dashboard";
import { InsightPage } from "./pages/Insight";
import { ActionPage } from "./pages/Action";
import { BenchmarkPage } from "./pages/Benchmark";
import { DataManagementPage } from "./pages/DataManagement";
import { AssistantPage } from "./pages/Assistant";

export const PRODUCT_NAME = "Dashboard Intelligent";

// The six destinations, in the owner's order, with the owner's labels. The
// short labels only apply to the compact chrome below 1024, where a 320px
// bottom bar gives each item a fifth of the width; the drawer always shows
// the full label (owner's call, 2026-10-01).
const DESTINATIONS: Omit<ConsoleDestination, "badge">[] = [
  { id: "dashboard", label: "Dashboard", shortLabel: "Dasbor", href: ROUTES.dashboard, icon: "dashboard" },
  { id: "insight", label: "Insight", href: ROUTES.insight, icon: "insight" },
  { id: "action", label: "Action", href: ROUTES.action, icon: "action" },
  { id: "benchmark", label: "Competitor Benchmark", shortLabel: "Pesaing", href: ROUTES.benchmark, icon: "benchmark" },
  { id: "data", label: "Data Management", shortLabel: "Data", href: ROUTES.data, icon: "data" },
  { id: "ai", label: "AI Assistant", shortLabel: "AI", href: ROUTES.ai, icon: "ai" },
];

const PAGES: Record<RouteId, ComponentType> = {
  dashboard: DashboardPage,
  insight: InsightPage,
  action: ActionPage,
  benchmark: BenchmarkPage,
  data: DataManagementPage,
  ai: AssistantPage,
};

function currentHost(): string {
  return typeof location === "undefined" ? "localhost" : location.hostname;
}

export function App() {
  const pathname = usePathname();
  const route = routeFromPath(pathname);
  const tenant = tenantFrom({ hostname: currentHost(), pathname });
  const slug = tenant.slug;
  const industries = useApi(endpoints.industries);

  if (slug === null) {
    return (
      <ConsoleShell title={PRODUCT_NAME} destinations={withPrefix(DESTINATIONS, tenant)} current={route}>
        <UnknownIndustry />
      </ConsoleShell>
    );
  }

  const all = industries.status === "ready" ? industries.data : [];
  const summary = all.find((industry) => industry.slug === slug) ?? null;

  return (
    <IndustryProvider value={{ slug, summary, all }}>
      <IndustryConsole key={slug} slug={slug} tenant={tenant} shortName={summary?.shortName ?? null} route={route} />
    </IndustryProvider>
  );
}

/** The destinations' links inside the current tenant: "/insight" -> "/pmb/insight". */
function withPrefix<T extends { href: string }>(items: T[], tenant: Tenant): T[] {
  if (!tenant.prefix) return items;
  return items.map((d) => ({ ...d, href: d.href === "/" ? tenant.prefix : `${tenant.prefix}${d.href}` }));
}

function IndustryConsole({
  slug,
  tenant,
  shortName,
  route,
}: {
  slug: IndustrySlug;
  tenant: Tenant;
  shortName: string | null;
  route: RouteId;
}) {
  // The Insight badge counts this industry's findings that need action, so
  // the nav itself says where attention is owed. A failed fetch shows none.
  const insights = useApi(() => endpoints.insights(slug));
  const attention = insights.status === "ready" ? insights.data.filter((i) => i.verdict === "attention").length : 0;
  const destinations: ConsoleDestination[] = withPrefix(DESTINATIONS, tenant).map((d) =>
    d.id === "insight" && attention > 0 ? { ...d, badge: attention } : d,
  );

  useEffect(() => {
    if (shortName) document.title = `${shortName} | ${PRODUCT_NAME}`;
  }, [shortName]);

  const Page = PAGES[route];
  return (
    <ConsoleShell
      title={PRODUCT_NAME}
      meta={shortName ?? undefined}
      switcher={<IndustrySwitcher />}
      destinations={destinations}
      current={route}
      fill={route === "ai"}
    >
      <Page />
    </ConsoleShell>
  );
}

function UnknownIndustry() {
  return (
    <>
      <PageHeader title="Industri tidak dikenal" description={`Alamat "${currentHost()}" tidak cocok dengan industri mana pun.`} />
      <EmptyState title="Pilih salah satu industri">
        <ul className="industry-links">
          {INDUSTRY_SLUGS.map((s) => (
            <li key={s}>
              <a href={typeof location === "undefined" ? "#" : industryUrl(s, location)}>{s}</a>
            </li>
          ))}
        </ul>
      </EmptyState>
    </>
  );
}
